/* Options → Trade Planner sub-tab.
   Takes a ticker + live Polygon context and produces a complete
   trade plan for any of 7 supported strategies (Call/Put Debit,
   Call/Put Credit, Iron Condor, Covered Call, Cash Secured Put).

   The strategy dropdown is the master input — switching strategies
   re-renders the strike inputs (1, 2, or 4 fields), re-labels the
   premium field (Max Debit ↔ Credit Received), regenerates auto-
   filled strikes and recomputes Trade Math + Expiration P/L Table. */

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { C } from "./_shared";
import { fetchPrice, fetchBars, TickerSearch } from "./_prices";
import { useCompanyNames } from "./_companyNames";
import { fetchEarnings } from "./_earnings";
import { useAccountSize } from "./_accountSize";
import {
  API, STRATEGIES, BIASES,
  apiJSON, computePlanner, expirationRows, computeAutoFill, strategyMeta,
  S, Stat,
} from "./_optionsShared";

// FR = Field with Reset. HOISTED to module scope and memoised so editing
// any field doesn't unmount-and-remount every other field on every keystroke
// (which kills focus mid-typing — the original "field resets after each
// digit" bug). Pass `onChange` and `onReset` as stable callbacks.
const FR = React.memo(function FR({
  label, value, fieldKey, type = "number", placeholder = "", testid,
  overridden, onChange, onReset,
}) {
  // type="text" + inputMode="decimal" gives us the same numeric mobile
  // keyboard as type="number" but without browser-specific focus glitches
  // (Safari resetting cursor, Chrome scroll-wheel value mutation, etc.)
  // For dates we still use a real date input.
  const isText = type === "number";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <label style={{ fontSize: 10, color: C.gray, fontWeight: 500 }}>{label}</label>
        {overridden ? (
          <button
            type="button"
            onClick={() => onReset(fieldKey)}
            data-testid={`reset-field-${fieldKey}`}
            title="Reset to auto value"
            style={{
              fontSize: 9, padding: "1px 5px", borderRadius: 3,
              background: "#e0e7ff", color: C.it, border: `0.5px solid ${C.indigo}55`,
              cursor: "pointer", fontWeight: 600, lineHeight: 1.1,
            }}
          >↺ reset</button>
        ) : (
          <span
            data-testid={`auto-badge-${fieldKey}`}
            title="Auto-filled — edit to override"
            style={{
              fontSize: 8, padding: "1px 4px", borderRadius: 3,
              background: "#f3f4f6", color: C.gray, fontWeight: 600, lineHeight: 1.1,
            }}
          >auto</span>
        )}
      </div>
      <input
        type={isText ? "text" : type}
        inputMode={isText ? "decimal" : undefined}
        autoComplete="off"
        value={value}
        onChange={e => onChange(fieldKey, e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        style={{
          padding: "7px 9px", borderRadius: 6, fontSize: 12,
          border: overridden ? `1.5px solid ${C.indigo}` : "0.5px solid #d1d5db",
          background: overridden ? "#eef2ff" : "#fff",
        }}
      />
    </div>
  );
});

export default function OptionsTradePlanner({ prefilledTicker, onLogTrade }) {
  const EMPTY = {
    ticker: "", bias: "Bullish", strategy: "Call Debit Spread",
    expiration: "", current_price: "", entry_trigger: "", entry_method: "", invalidation: "",
    // Two-leg vertical inputs:
    buy_strike: "", sell_strike: "",
    // Iron Condor inputs:
    put_buy_strike: "", put_sell_strike: "", call_sell_strike: "", call_buy_strike: "",
    // Covered Call:
    cost_basis: "",
    // Generic strike slots used by all non-legacy strategies (k1..k4)
    k1: "", k2: "", k3: "", k4: "",
    // Ratio selector for ratio spreads & backspreads (2 or 3)
    ratio_n: "2",
    // Second expiration for calendar spreads
    expiration2: "",
    max_debit: "", qty: "1",
  };
  const [plan, setPlan] = useState(EMPTY);
  const [ctx, setCtx] = useState({ price: 0, dma20: 0, dma50: 0, vol: 0, avgVol: 0, high10: 0, high5: 0, low10: 0, loaded: false });
  // ── Which ticker we've already auto-filled for. Ref (not state) so the
  // setInterval closure below always sees the CURRENT value — a state-based
  // flag would be captured stale and re-trigger auto-fill every poll, which
  // was the exact bug that wiped manual strike edits every 10 seconds.
  const autofilledForRef = useRef(null);
  const [refreshToken, setRefreshToken]   = useState(0);
  // ── userOverrides: field-names the user has manually edited.
  // Auto-fill (first-fill / strategy-change / refresh) must NEVER overwrite
  // fields in this set. `set(k)` marks a field overridden on every edit;
  // `resetField(k)` removes a field and re-computes its auto value from
  // the last known ctx + strategy; `resetAll()` wipes the set entirely
  // and runs a full auto-fill.
  const [userOverrides, setUserOverrides] = useState({});
  const isOverridden = (k) => !!userOverrides[k];
  const { name: companyName } = useCompanyNames();
  const { accountRaw, setAccount, account: accountSize } = useAccountSize();
  const meta = useMemo(() => strategyMeta(plan.strategy), [plan.strategy]);

  // Prefill from watchlist ticker click
  useEffect(() => {
    if (prefilledTicker && prefilledTicker !== plan.ticker) {
      setPlan(p => ({ ...p, ticker: prefilledTicker }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefilledTicker]);

  // ── Live market context (ticker change = initial load; interval = price only)
  //
  // PER THE MANUAL-OVERRIDE CONTRACT:
  //   - Auto-fill runs ONLY when a NEW ticker is loaded (firstLoad path below),
  //     or when the user explicitly clicks Refresh / Reset buttons (which bump
  //     `refreshToken` and clear `autofilledForRef`).
  //   - The 10-second price polling interval NEVER calls computeAutoFill and
  //     NEVER mutates `plan` — it only refreshes `ctx.price` + `ctx.vol` so
  //     the live-data display (20/50 DMA readout, Entry Checklist volume row,
  //     Trade Math) reacts to the latest tape.
  //   - Stale-closure-safe: we compare against `autofilledForRef.current`,
  //     not the `autofilledFor` state that was captured when the effect ran.
  useEffect(() => {
    const t = (plan.ticker || "").trim().toUpperCase();
    if (!t) {
      setCtx({ price: 0, dma20: 0, dma50: 0, vol: 0, avgVol: 0, high10: 0, high5: 0, low10: 0, loaded: false });
      return;
    }
    let cancelled = false;

    // Initial load — fetches daily bars (for DMA + entry triggers) and the
    // live price, then runs auto-fill EXACTLY ONCE per ticker.
    async function initialLoad() {
      try {
        const [priceData, bars] = await Promise.all([
          fetchPrice(t),
          fetchBars(t, { timespan: "day", multiplier: 1, limit: 60 }),
        ]);
        if (cancelled) return;
        const closes = (bars || []).map(b => +b.close).filter(Number.isFinite);
        const vols   = (bars || []).map(b => +b.vol).filter(Number.isFinite);
        const highs  = (bars || []).map(b => +b.high).filter(Number.isFinite);
        const lows   = (bars || []).map(b => +b.low).filter(Number.isFinite);
        const avg = (arr, n) => arr.length >= n ? arr.slice(-n).reduce((a, b) => a + b, 0) / n : 0;
        const dma20  = avg(closes, 20);
        const dma50  = avg(closes, 50);
        const avgVol = avg(vols, 20);
        const high10 = highs.length >= 10 ? Math.max(...highs.slice(-10)) : 0;
        const high5  = highs.length >= 5  ? Math.max(...highs.slice(-5))  : 0;
        const low10  = lows.length  >= 10 ? Math.min(...lows.slice(-10))  : 0;
        const price  = priceData?.ok ? priceData.price : (closes.length ? closes[closes.length - 1] : 0);
        const vol    = priceData?.ok ? (priceData.vol || 0) : (vols.length ? vols[vols.length - 1] : 0);
        const newCtx = { price, dma20, dma50, vol, avgVol, high10, high5, low10, loaded: true };
        setCtx(newCtx);

        // Auto-fill gate: run ONLY if this ticker hasn't been auto-filled
        // in the current session. `autofilledForRef.current` is the live
        // value (no stale-closure risk).
        if (autofilledForRef.current !== t && price > 0) {
          autofilledForRef.current = t;
          const suggested = computeAutoFill(newCtx, plan.strategy);
          setUserOverrides({});
          setPlan(p => ({ ...p, ...suggested, ticker: t }));
        }
      } catch { /* noop */ }
    }

    // Refresh-only — fires every 10s, touches ONLY `ctx`. Never `plan`,
    // never `computeAutoFill`. The form is the user's, not the market's.
    async function refreshPriceOnly() {
      try {
        const priceData = await fetchPrice(t);
        if (cancelled || !priceData?.ok) return;
        setCtx(c => ({
          ...c,
          price: priceData.price || c.price,
          vol:   priceData.vol   || c.vol,
        }));
      } catch { /* noop */ }
    }

    initialLoad();
    const id = setInterval(refreshPriceOnly, 10 * 1000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan.ticker, refreshToken]);

  // ── Re-autofill strike inputs whenever strategy changes (after initial load).
  // We keep the user's expiration / bias / qty / trigger / invalidation but
  // wipe the strike + premium fields and re-suggest them per the new strategy.
  // User-overridden fields are preserved. The strategy dropdown itself counts
  // as a "reset signal" for the strike family — when the user picks a new
  // strategy we drop strike-related overrides so the new strategy's auto-fill
  // actually takes effect; the user can then edit again as needed.
  const prevStrategyRef = useRef(plan.strategy);
  useEffect(() => {
    if (prevStrategyRef.current === plan.strategy) return;
    prevStrategyRef.current = plan.strategy;
    if (!ctx.loaded || !ctx.price) return;
    const suggested = computeAutoFill(ctx, plan.strategy);
    // Drop overrides for fields that this strategy's auto-fill controls —
    // strikes, premium, bias, entry_method. Other overrides (expiration,
    // qty, current_price, entry_trigger, invalidation) stay intact.
    setUserOverrides(prev => {
      const next = { ...prev };
      ["bias", "buy_strike", "sell_strike", "put_buy_strike", "put_sell_strike",
       "call_sell_strike", "call_buy_strike", "cost_basis", "max_debit", "entry_method",
       "k1", "k2", "k3", "k4", "ratio_n", "expiration2",
      ].forEach(k => delete next[k]);
      return next;
    });
    setPlan(p => ({
      ...p,
      bias:           suggested.bias            || p.bias,
      buy_strike:     suggested.buy_strike      || "",
      sell_strike:    suggested.sell_strike     || "",
      put_buy_strike:   suggested.put_buy_strike   || "",
      put_sell_strike:  suggested.put_sell_strike  || "",
      call_sell_strike: suggested.call_sell_strike || "",
      call_buy_strike:  suggested.call_buy_strike  || "",
      cost_basis:     suggested.cost_basis      || "",
      k1: suggested.k1 || "", k2: suggested.k2 || "",
      k3: suggested.k3 || "", k4: suggested.k4 || "",
      ratio_n:     suggested.ratio_n     || p.ratio_n || "2",
      expiration2: suggested.expiration2 || p.expiration2 || "",
      max_debit:      suggested.max_debit       || "",
      entry_method:   suggested.entry_method    || p.entry_method,
    }));
  }, [plan.strategy, ctx]);

  function refreshLiveData() {
    // Hard refresh — user clicked the button, treat it as "reset all to auto".
    setUserOverrides({});
    autofilledForRef.current = null;    // force initialLoad to re-autoFill
    setRefreshToken(n => n + 1);
  }

  // ── Full reset: drop all overrides + re-run auto-fill.
  function resetAll() {
    if (!ctx.loaded || !ctx.price) {
      // Nothing to reset to yet — equivalent to a refresh.
      refreshLiveData();
      return;
    }
    const suggested = computeAutoFill(ctx, plan.strategy);
    setUserOverrides({});
    setPlan(p => ({ ...p, ...suggested, ticker: p.ticker }));
  }

  // ── Earnings lookup (Polygon): warns if earnings fall before expiration.
  const [earnings, setEarnings] = useState(null);
  useEffect(() => {
    const t = (plan.ticker || "").trim().toUpperCase();
    if (!t) { setEarnings(null); return; }
    let cancelled = false;
    (async () => {
      const e = await fetchEarnings(t);
      if (!cancelled) setEarnings(e);
    })();
    return () => { cancelled = true; };
  }, [plan.ticker]);
  const earningsBeforeExpiry = (() => {
    if (!earnings?.next_earnings_date || !plan.expiration) return null;
    try {
      const ne = new Date(earnings.next_earnings_date + "T00:00:00-05:00");
      const ex = new Date(plan.expiration + "T16:00:00-05:00");
      if (ne < ex && ne >= new Date(Date.now() - 24*3600*1000)) return earnings;
    } catch { /* noop */ }
    return null;
  })();

  const math = useMemo(() => computePlanner(plan), [plan]);
  const plRows = useMemo(() => expirationRows(plan), [plan]);

  // ── Stable FR callbacks (referential equality preserved across renders
  // so React.memo on FR can short-circuit re-renders of fields the user
  // ISN'T currently typing in — fixes the "field resets after each digit"
  // focus-loss bug caused by re-mounting inputs every keystroke).
  const handleFieldChange = useCallback((k, v) => {
    setUserOverrides(prev => prev[k] ? prev : { ...prev, [k]: true });
    setPlan(p => ({ ...p, [k]: v }));
  }, []);
  const ctxRef = useRef(ctx);
  const stratRef = useRef(plan.strategy);
  useEffect(() => { ctxRef.current = ctx; stratRef.current = plan.strategy; }, [ctx, plan.strategy]);
  const handleFieldReset = useCallback((k) => {
    const c = ctxRef.current;
    if (!c?.loaded || !c.price) return;
    const suggested = computeAutoFill(c, stratRef.current);
    setUserOverrides(prev => {
      const next = { ...prev };
      delete next[k];
      return next;
    });
    setPlan(p => ({ ...p, [k]: suggested[k] ?? "" }));
  }, []);   // empty deps — refs keep this stable across all renders

  // ── Automated Entry Checklist (5 live rules) ──
  const autoChecks = useMemo(() => {
    const price = ctx.price || +plan.current_price || 0;
    let trend;
    if (!price || !ctx.dma20 || !ctx.dma50) trend = { status: "WAIT", note: "Loading bars…" };
    else if (price > ctx.dma20 && price > ctx.dma50) trend = { status: "PASS", note: `Price $${price.toFixed(2)} · 20 DMA $${ctx.dma20.toFixed(2)} · 50 DMA $${ctx.dma50.toFixed(2)} ✓` };
    else                                              trend = { status: "FAIL", note: `Price $${price.toFixed(2)} below 20 DMA $${ctx.dma20.toFixed(2)} or 50 DMA $${ctx.dma50.toFixed(2)}` };

    const trig = +plan.entry_trigger || 0;
    let trigger;
    if (!price || !trig) trigger = { status: "WAIT", note: "Set trigger price" };
    else if (price >= trig) trigger = { status: "PASS", note: `Current $${price.toFixed(2)} above trigger $${trig.toFixed(2)} ✓` };
    else                    trigger = { status: "WAIT", note: `Current $${price.toFixed(2)} · Trigger $${trig.toFixed(2)} — Waiting for confirmation` };

    let volume;
    if (!ctx.vol || !ctx.avgVol) volume = { status: "WAIT", note: "Loading volume…" };
    else {
      const ratio = ctx.vol / ctx.avgVol;
      const fmt = (v) => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : `${(v/1e3).toFixed(0)}K`;
      if (ratio >= 0.8)      volume = { status: "PASS", note: `Today ${fmt(ctx.vol)} · 20-day avg ${fmt(ctx.avgVol)} · Expanding ✓` };
      else if (ratio >= 0.6) volume = { status: "WARN", note: `Today ${fmt(ctx.vol)} · 20-day avg ${fmt(ctx.avgVol)} · Below average` };
      else                   volume = { status: "FAIL", note: `Today ${fmt(ctx.vol)} · 20-day avg ${fmt(ctx.avgVol)} · Volume too weak` };
    }

    // Risk thresholds: < 5% PASS · 5–10% WARN · > 10% FAIL.
    const risk = (() => {
      if (!math.capitalAtRisk) return { status: "WAIT", note: "Enter strikes + premium" };
      if (!accountSize)         return { status: "WARN", note: "⚠ Enter account size to check risk" };
      const pct = math.capitalAtRisk / accountSize * 100;
      if (pct < 5)   return { status: "PASS", note: `Capital at Risk $${math.capitalAtRisk.toFixed(0)} · ${pct.toFixed(2)}% of $${accountSize.toLocaleString()} account ✓` };
      if (pct <= 10) return { status: "WARN", note: `Capital at Risk $${math.capitalAtRisk.toFixed(0)} · ${pct.toFixed(2)}% of account — above 5% comfort` };
      return             { status: "FAIL", note: `Capital at Risk $${math.capitalAtRisk.toFixed(0)} · ${pct.toFixed(2)}% of account — too high, reduce size` };
    })();

    let timing;
    if (!math.dte) timing = { status: "WAIT", note: "Set expiration date" };
    else if (math.dte >= 45 && math.dte <= 90) timing = { status: "PASS", note: `${math.dte} days to expiration ✓ — Ideal range` };
    else if (math.dte >= 30 && math.dte < 45)  timing = { status: "WARN", note: `${math.dte} days — below 45 DTE sweet spot` };
    else if (math.dte > 90)                    timing = { status: "WARN", note: `${math.dte} days — longer than 90 DTE` };
    else                                        timing = { status: "FAIL", note: `${math.dte} days — too close to expiration` };

    return { trend, trigger, volume, risk, timing };
  }, [ctx, plan.current_price, plan.entry_trigger, math.capitalAtRisk, math.dte, accountSize]);

  const autoPassCount = Object.values(autoChecks).filter(c => c.status === "PASS").length;
  const anyFail       = Object.values(autoChecks).some(c => c.status === "FAIL");

  // Enable Save when minimum required strikes for the active strategy are set.
  const minStrikesSet = (() => {
    if (meta.generic) {
      return (meta.strikeSlots || []).every(s => +plan[s.k]);
    }
    if (meta.kind === "iron_condor")
      return +plan.put_buy_strike && +plan.put_sell_strike && +plan.call_sell_strike && +plan.call_buy_strike;
    if (meta.kind === "covered_call")     return +plan.sell_strike && +plan.cost_basis;
    if (meta.kind === "cash_secured_put") return +plan.sell_strike;
    return +plan.buy_strike && +plan.sell_strike;
  })();

  async function saveAsTrade() {
    if (math.dte > 0 && math.dte < 7) {
      try {
        await fetch(`${API}/api/stock/alert/telegram`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message:
            `⚠️ <b>OPTIONS WARNING</b> — Holding into final expiration week on <b>${plan.ticker.toUpperCase()}</b>. Rule: Exit unless near max value.`
          }),
        });
      } catch { /* noop */ }
    }

    const body = {
      date: new Date().toISOString().slice(0, 10),
      ticker: plan.ticker.toUpperCase(),
      bias: plan.bias,
      strategy: plan.strategy,
      expiration: plan.expiration,
      expiration2: plan.expiration2 || "",
      ratio_n: +plan.ratio_n || 0,
      entry_trigger: +plan.entry_trigger || 0,
      invalidation:  +plan.invalidation  || 0,
      buy_strike:  +plan.buy_strike  || 0,
      sell_strike: +plan.sell_strike || 0,
      put_buy_strike:   +plan.put_buy_strike   || 0,
      put_sell_strike:  +plan.put_sell_strike  || 0,
      call_sell_strike: +plan.call_sell_strike || 0,
      call_buy_strike:  +plan.call_buy_strike  || 0,
      k1: +plan.k1 || 0, k2: +plan.k2 || 0, k3: +plan.k3 || 0, k4: +plan.k4 || 0,
      cost_basis: +plan.cost_basis || 0,
      // Premium routing: store under entry_debit for debit strategies and
      // under entry_credit for credit strategies — keeps the options
      // monitor cron (which reads entry_debit) from misinterpreting credit trades.
      entry_debit:  meta.isCredit ? 0 : (+plan.max_debit || 0),
      entry_credit: meta.isCredit ? (+plan.max_debit || 0) : 0,
      qty: +plan.qty || 1,
      max_risk: math.capitalAtRisk,
      profit_target: math.pt50,
      stop_price: math.sl40,
      notes: meta.kind === "iron_condor" || math.breakeven2
        ? `BE ${(math.breakeven||0).toFixed(2)}–${(math.breakeven2||0).toFixed(2)} · R:R ${(math.rr||0).toFixed(2)}`
        : `Breakeven $${(math.breakeven||0).toFixed(2)} · R:R ${(math.rr||0).toFixed(2)}`,
      status: "Open",
    };
    try {
      await apiJSON("/api/trading/options_trades", { method: "POST", body: JSON.stringify(body) });
      if (typeof onLogTrade === "function") onLogTrade();
      alert(`Logged ${plan.ticker} ${plan.strategy} to trade log.`);
    } catch (e) {
      alert("Save failed: " + e.message);
    }
  }

  // ── Render the strike + premium fields specific to the chosen strategy ──
  function StrikeFields() {
    if (meta.generic) {
      return (
        <>
          {(meta.strikeSlots || []).map(slot => (
            <FR key={slot.k}
                label={slot.label}
                value={plan[slot.k]}
                fieldKey={slot.k}
                testid={`options-strike-${slot.k}`}
                overridden={isOverridden(slot.k)} onChange={handleFieldChange} onReset={handleFieldReset}/>
          ))}
          {(meta.extraFields || []).includes("ratio_n") && (
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <label style={{ fontSize: 10, color: C.gray, fontWeight: 500 }}>Ratio (short legs per long)</label>
              <select
                value={plan.ratio_n || "2"}
                onChange={e => handleFieldChange("ratio_n", e.target.value)}
                data-testid="options-ratio-n"
                style={{ padding:"7px 9px", borderRadius:6, border:"0.5px solid #d1d5db", fontSize:12, background:"#fff" }}
              >
                <option value="2">1×2 (standard)</option>
                <option value="3">1×3 (aggressive)</option>
              </select>
            </div>
          )}
          {(meta.extraFields || []).includes("expiration2") && (
            <FR label="Far Expiration" value={plan.expiration2 || ""} fieldKey="expiration2" type="date" testid="options-expiration2" overridden={isOverridden("expiration2")} onChange={handleFieldChange} onReset={handleFieldReset}/>
          )}
          {(meta.extraFields || []).includes("cost_basis") && (
            <FR label="Share Cost Basis" value={plan.cost_basis} fieldKey="cost_basis" testid="options-cost-basis" overridden={isOverridden("cost_basis")} onChange={handleFieldChange} onReset={handleFieldReset}/>
          )}
          {meta.note && (
            <div style={{ gridColumn:"1 / -1", fontSize:10, color:C.amber, fontWeight:600 }}>ℹ {meta.note}</div>
          )}
        </>
      );
    }
    if (meta.kind === "iron_condor") {
      return (
        <>
          <FR label="Put Buy Strike (lower)"   value={plan.put_buy_strike}   fieldKey="put_buy_strike"   testid="options-put-buy-strike" overridden={isOverridden("put_buy_strike")} onChange={handleFieldChange} onReset={handleFieldReset}/>
          <FR label="Put Sell Strike"          value={plan.put_sell_strike}  fieldKey="put_sell_strike"  testid="options-put-sell-strike" overridden={isOverridden("put_sell_strike")} onChange={handleFieldChange} onReset={handleFieldReset}/>
          <FR label="Call Sell Strike"         value={plan.call_sell_strike} fieldKey="call_sell_strike" testid="options-call-sell-strike" overridden={isOverridden("call_sell_strike")} onChange={handleFieldChange} onReset={handleFieldReset}/>
          <FR label="Call Buy Strike (upper)"  value={plan.call_buy_strike}  fieldKey="call_buy_strike"  testid="options-call-buy-strike" overridden={isOverridden("call_buy_strike")} onChange={handleFieldChange} onReset={handleFieldReset}/>
        </>
      );
    }
    if (meta.kind === "covered_call") {
      return (
        <>
          <FR label="Share Cost Basis" value={plan.cost_basis}  fieldKey="cost_basis"  testid="options-cost-basis" overridden={isOverridden("cost_basis")} onChange={handleFieldChange} onReset={handleFieldReset}/>
          <FR label="Call Sell Strike" value={plan.sell_strike} fieldKey="sell_strike" testid="options-sell-strike" overridden={isOverridden("sell_strike")} onChange={handleFieldChange} onReset={handleFieldReset}/>
        </>
      );
    }
    if (meta.kind === "cash_secured_put") {
      return (
        <FR label="Put Sell Strike" value={plan.sell_strike} fieldKey="sell_strike" testid="options-sell-strike" overridden={isOverridden("sell_strike")} onChange={handleFieldChange} onReset={handleFieldReset}/>
      );
    }
    // Vertical spreads (debit + credit) — show both legs.
    return (
      <>
        <FR label="Buy Strike"  value={plan.buy_strike}  fieldKey="buy_strike"  testid="options-buy-strike" overridden={isOverridden("buy_strike")} onChange={handleFieldChange} onReset={handleFieldReset}/>
        <FR label="Sell Strike" value={plan.sell_strike} fieldKey="sell_strike" testid="options-sell-strike" overridden={isOverridden("sell_strike")} onChange={handleFieldChange} onReset={handleFieldReset}/>
      </>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      <div data-testid="options-rules-summary" style={{ background: "linear-gradient(135deg, #eef2ff, #dbeafe)", border: "0.5px solid #c7d2fe", borderRadius: 10, padding: "10px 14px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 6 }}>
          <div style={{ fontSize: 11, color: "#1e1b4b" }}><b style={{ color: "#4338ca" }}>Core:</b> Vertical debit spreads only</div>
          <div style={{ fontSize: 11, color: "#1e1b4b" }}><b style={{ color: "#4338ca" }}>Entry:</b> Confirm trigger · Limit orders only · 45–90 DTE</div>
          <div style={{ fontSize: 11, color: "#1e1b4b" }}><b style={{ color: "#4338ca" }}>Exit:</b> Take profit 50–80% · Cut loss 40–50%</div>
          <div style={{ fontSize: 11, color: "#1e1b4b" }}><b style={{ color: "#4338ca" }}>No trade:</b> Wide spreads · Chasing · Stuck price</div>
        </div>
      </div>

      <div data-testid="options-account-size-card" style={{
          background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10,
          padding: "12px 16px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
        }}>
          <div style={{ flex: "0 0 auto" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#111" }}>💼 Account Size</div>
            <div style={{ fontSize: 10, color: C.gray, marginTop: 2 }}>
              Shared with Risk Tools · Risk thresholds: <span style={{ color: C.green, fontWeight: 700 }}>&lt;5% PASS</span> · <span style={{ color: C.amber, fontWeight: 700 }}>5–10% WARN</span> · <span style={{ color: C.red, fontWeight: 700 }}>&gt;10% FAIL</span>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 13, color: C.gray, fontWeight: 600 }}>$</span>
            <input
              type="number"
              inputMode="decimal"
              value={accountRaw}
              onChange={e => setAccount(e.target.value)}
              placeholder="Enter your account size"
              data-testid="options-account-size-input"
              style={{
                padding: "8px 12px", borderRadius: 7,
                border: `1px solid ${accountSize > 0 ? "#d1d5db" : C.amber}`,
                fontSize: 14, fontWeight: 700, width: 170, background: "#fff",
              }}
            />
          </div>
          {accountSize > 0 && (
            <div style={{ fontSize: 11, color: C.gray, marginLeft: "auto" }}>
              5% = <b style={{ color: "#111" }}>${(accountSize * 0.05).toFixed(0)}</b> max comfort ·
              {" "}10% = <b style={{ color: "#111" }}>${(accountSize * 0.10).toFixed(0)}</b> hard limit
            </div>
          )}
        </div>

      {earningsBeforeExpiry && (
        <div data-testid="options-earnings-warning" style={{
          padding:"12px 16px", borderRadius:10,
          background:"#fef2f2", border:`1px solid ${C.red}66`,
          display:"flex", alignItems:"center", gap:10, flexWrap:"wrap",
        }}>
          <span style={{ fontSize:18 }}>⚠️</span>
          <div style={{ flex:1, minWidth:220 }}>
            <div style={{ fontSize:12, fontWeight:800, color:C.red }}>
              EARNINGS WARNING — <b>{plan.ticker.toUpperCase()}</b> reports on {earningsBeforeExpiry.next_earnings_date}
              {typeof earningsBeforeExpiry.days_until === "number" && earningsBeforeExpiry.days_until >= 0 && (
                <> ({earningsBeforeExpiry.days_until === 0 ? "today" : `in ${earningsBeforeExpiry.days_until} day${earningsBeforeExpiry.days_until !== 1 ? "s" : ""}`})</>
              )}
            </div>
            <div style={{ fontSize:11, color:"#7f1d1d", marginTop:3 }}>
              Earnings falls <b>before</b> expiration ({plan.expiration}). Rule: never hold options through earnings. Shorten the expiry or skip the trade.
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 14 }}>

        <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "14px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
            <p style={{ fontSize: 13, fontWeight: 700, color: "#111" }}>🎯 Trade Setup</p>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {Object.keys(userOverrides).length > 0 && (
                <span data-testid="options-override-count" style={{ fontSize: 10, color: C.it, background: "#e0e7ff", padding: "2px 8px", borderRadius: 4, fontWeight: 600 }}>
                  {Object.keys(userOverrides).length} manual override{Object.keys(userOverrides).length === 1 ? "" : "s"}
                </span>
              )}
              <button onClick={resetAll} data-testid="options-reset-all-btn"
                disabled={Object.keys(userOverrides).length === 0}
                style={{
                  padding: "4px 10px", borderRadius: 6,
                  background: Object.keys(userOverrides).length === 0 ? "#f3f4f6" : "#fff",
                  color: Object.keys(userOverrides).length === 0 ? C.gray : C.it,
                  border: `0.5px solid ${Object.keys(userOverrides).length === 0 ? "#e5e7eb" : C.indigo + "55"}`,
                  fontSize: 10, fontWeight: 700,
                  cursor: Object.keys(userOverrides).length === 0 ? "not-allowed" : "pointer",
                }}
              >↺ Reset all to auto</button>
              {plan.ticker && (
                <button onClick={refreshLiveData} data-testid="options-refresh-btn"
                  style={{ padding: "4px 10px", borderRadius: 6, background: C.ib, color: C.it, border: `0.5px solid ${C.indigo}33`, fontSize: 10, fontWeight: 700, cursor: "pointer" }}>
                  🔄 Refresh live data
                </button>
              )}
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ fontSize: 10, color: C.gray, fontWeight: 500, display: "block", marginBottom: 4 }}>Ticker</label>
            <TickerSearch
              onSelect={item => setPlan(p => ({ ...p, ticker: item.symbol }))}
              placeholder='Type "Microsoft" or "MSFT"…'
            />
            {plan.ticker && (
              <div style={{ fontSize: 11, color: C.gray, marginTop: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <b>{plan.ticker.toUpperCase()}</b> · {companyName(plan.ticker, "")}
                {ctx.loaded && ctx.dma20 > 0 && (
                  <span data-testid="options-dma-readout" style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>
                    20MA ${ctx.dma20.toFixed(2)} · 50MA ${ctx.dma50.toFixed(2)}
                  </span>
                )}
              </div>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8 }}>
            {S({ label: "Bias", value: plan.bias, onChange: (v) => handleFieldChange("bias", v), options: BIASES, testid: "options-bias" })}
            {S({ label: "Strategy", value: plan.strategy, onChange: (v) => handleFieldChange("strategy", v), options: STRATEGIES, testid: "options-strategy" })}
            <FR label="Expiration"          value={plan.expiration}    fieldKey="expiration"    type="date" testid="options-expiration" overridden={isOverridden("expiration")} onChange={handleFieldChange} onReset={handleFieldReset}/>
            <FR label="Current Stock Price" value={plan.current_price} fieldKey="current_price" placeholder="auto-filled" testid="options-current-price" overridden={isOverridden("current_price")} onChange={handleFieldChange} onReset={handleFieldReset}/>
            <FR label="Entry Trigger $"     value={plan.entry_trigger} fieldKey="entry_trigger" testid="options-entry-trigger" overridden={isOverridden("entry_trigger")} onChange={handleFieldChange} onReset={handleFieldReset}/>
            <FR label="Chart Invalidation $" value={plan.invalidation} fieldKey="invalidation"  testid="options-invalidation" overridden={isOverridden("invalidation")} onChange={handleFieldChange} onReset={handleFieldReset}/>
            <StrikeFields/>
            <FR label={meta.premiumLabel} value={plan.max_debit} fieldKey="max_debit" placeholder="per contract" testid="options-max-debit" overridden={isOverridden("max_debit")} onChange={handleFieldChange} onReset={handleFieldReset}/>
            <FR label="Quantity"          value={plan.qty}       fieldKey="qty"                                  testid="options-qty" overridden={isOverridden("qty")} onChange={handleFieldChange} onReset={handleFieldReset}/>
          </div>
          {autofilledForRef.current && plan.entry_method && (
            <div data-testid="options-entry-method-hint" style={{ marginTop: 8, padding: "6px 10px", borderRadius: 6, background: C.ib, border: `0.5px solid ${C.indigo}33`, color: C.it, fontSize: 10, fontWeight: 600 }}>
              Entry trigger method: <b>{plan.entry_method}</b>
            </div>
          )}
          {autofilledForRef.current && plan.max_debit && (
            <div data-testid="options-max-debit-hint" style={{ marginTop: 8, padding: "6px 10px", borderRadius: 6, background: "#fef3c7", border: "0.5px solid #fcd34d", color: "#92400e", fontSize: 10, fontWeight: 600 }}>
              ~${(+plan.max_debit).toFixed(2)} {meta.isCredit ? "credit est." : "est."} — verify with your broker before placing the order
            </div>
          )}
        </div>

        <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "14px 16px" }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 10 }}>🧮 Trade Math</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8 }}>
            <Stat label={meta.kind === "covered_call" ? "Strike − Basis" : meta.kind === "cash_secured_put" ? "Strike $" : "Spread Width"} value={`$${(math.spreadWidth||0).toFixed(2)}`}/>
            <Stat label="Capital at Risk" value={Number.isFinite(math.capitalAtRisk) ? `$${math.capitalAtRisk.toFixed(0)}` : "Unlimited"} color={C.red}/>
            <Stat label="Max Profit" value={Number.isFinite(math.maxProfit) ? `$${math.maxProfit.toFixed(0)}` : "Unlimited"} color={C.green}/>
            {(meta.kind === "iron_condor" || (math.breakeven2 && math.breakeven2 !== math.breakeven)) ? (
              <>
                <Stat label="BE Lower" value={`$${(math.breakeven||0).toFixed(2)}`}/>
                <Stat label="BE Upper" value={`$${(math.breakeven2||0).toFixed(2)}`}/>
              </>
            ) : (
              <Stat label="Breakeven" value={`$${(math.breakeven||0).toFixed(2)}`}/>
            )}
            <Stat label="Reward / Risk" value={Number.isFinite(math.rr) && math.rr > 0 ? `${math.rr.toFixed(2)}×` : (math.uncapped ? "∞" : "—")} color={math.rr >= 1 ? C.green : C.amber}/>
            <Stat label="Profit Target 50%" value={`$${(math.pt50||0).toFixed(0)}`} color={C.green}/>
            <Stat label="Profit Target 70%" value={`$${(math.pt70||0).toFixed(0)}`} color={C.green}/>
            <Stat label="Stop Loss 40%" value={`$${(math.sl40||0).toFixed(0)}`} color={C.red}/>
            <Stat label="Stop Loss 50%" value={`$${(math.sl50||0).toFixed(0)}`} color={C.red}/>
            <Stat label="Days to Expiration" value={`${math.dte}d`} color={math.dte >= 45 && math.dte <= 90 ? C.green : C.amber}/>
            <Stat label="Qty × 100" value={`${(+plan.qty || 1) * 100}`}/>
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 14 }}>
        <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "14px 16px" }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 10 }}>🤖 Automated Entry Checklist</p>
          {[
            ["trend",   "Trend",   "Price above 20-day and 50-day MA"],
            ["trigger", "Trigger", "Close above resistance/trigger"],
            ["volume",  "Volume",  "Expanding or stable"],
            ["risk",    "Risk",    "Capital at risk within plan"],
            ["timing",  "Timing",  "45–90 DTE preferred"],
          ].map(([k, label, condition], idx) => {
            const c = autoChecks[k];
            const badge = c.status === "PASS" ? { bg: C.gb, text: C.gt, l: "✓ PASS" }
                       : c.status === "WARN" ? { bg: C.ab, text: C.at, l: "⚠ WARN" }
                       : c.status === "FAIL" ? { bg: C.rb, text: C.rt, l: "✗ FAIL" }
                                             : { bg: C.gbg, text: C.gt2, l: "… WAIT" };
            return (
              <div key={k} data-testid={`options-autocheck-${k}`}
                style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 0", fontSize: 11, color: "#111", borderTop: idx > 0 ? "0.5px solid #f3f4f6" : "none" }}>
                <div style={{ minWidth: 62 }}>
                  <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: badge.bg, color: badge.text, fontWeight: 700 }}>
                    {badge.l}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#111" }}>{label}</div>
                  <div style={{ fontSize: 10, color: C.gray, marginBottom: 2 }}>{condition}</div>
                  <div style={{ fontSize: 10, color: C.muted, fontStyle: "italic" }}>{c.note}</div>
                </div>
              </div>
            );
          })}
          <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 7,
            background: anyFail ? C.rb : (autoPassCount === 5 ? C.gb : C.ab),
            color:      anyFail ? C.rt : (autoPassCount === 5 ? C.gt : C.at),
            fontSize: 11, fontWeight: 700 }}>
            {anyFail ? "⚠ Setup not confirmed — Do not enter yet" :
             autoPassCount === 5 ? "✅ All checks passed — Setup confirmed" :
             `${autoPassCount}/5 checks passed — Review before entry`}
          </div>
        </div>

        <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, padding: "14px 16px" }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: "#111", marginBottom: 10 }}>📋 Action Plan — {plan.strategy}</p>
          {meta.actionPlan.map(([k, v]) => (
            <div key={k} style={{ padding: "8px 0", borderTop: "0.5px solid #f3f4f6", display: "flex", gap: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.indigo, minWidth: 80 }}>{k}</span>
              <span style={{ fontSize: 11, color: C.gray, flex: 1 }}>{v}</span>
            </div>
          ))}
          <button onClick={saveAsTrade} disabled={!plan.ticker || !minStrikesSet} data-testid="options-plan-save-btn"
            style={{ marginTop: 10, width: "100%", padding: "8px 16px", borderRadius: 7, background: !plan.ticker || !minStrikesSet ? C.gbg : C.indigo, color: !plan.ticker || !minStrikesSet ? C.gray : "#fff", border: "none", fontSize: 12, fontWeight: 700, cursor: !plan.ticker || !minStrikesSet ? "not-allowed" : "pointer" }}>
            💾 Log this plan as an Open trade
          </button>
        </div>
      </div>

      {plRows.length > 0 && (
        <div style={{ background: "#fff", border: "0.5px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "10px 16px", borderBottom: "0.5px solid #f3f4f6", fontSize: 13, fontWeight: 700, color: "#111" }}>
            📊 Expiration P/L Table ({plan.strategy})
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  {["Stock Price @ Exp", "Long Value", "Short Value", "Strategy Value", "Net P/L", "Return on Risk", ""].map(h => (
                    <th key={h} style={{ padding: "6px 10px", textAlign: "left", color: C.gray, fontWeight: 500, fontSize: 10, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {plRows.map(r => {
                  const profit = r.netPL >= 0;
                  const marker = r.isCurrent ? { label: "← Current price", color: C.indigo, bg: C.ib } : r.marker;
                  const rowBg = marker ? marker.bg : (r.isRange ? "#fafbfc" : "transparent");
                  const priceLabel = r.isRange && r.sHi !== r.s
                    ? `$${r.s.toFixed(2)} – $${r.sHi.toFixed(2)}`
                    : `$${r.s.toFixed(2)}`;
                  const rangeTag = r.isRange && r.sHi !== r.s
                    ? <span style={{ fontSize: 9, color: C.gray, fontWeight: 600, marginLeft: 6 }}>(flat zone)</span>
                    : null;
                  return (
                    <tr key={`${r.s}-${r.sHi}`} style={{ borderTop: "0.5px solid #f3f4f6", background: rowBg }}>
                      <td style={{ padding: "6px 10px", fontWeight: marker ? 800 : 600, color: marker ? marker.color : "#111" }}>
                        {priceLabel}{rangeTag}
                      </td>
                      <td style={{ padding: "6px 10px", color: C.gray }}>${r.longVal.toFixed(0)}</td>
                      <td style={{ padding: "6px 10px", color: C.gray }}>${r.shortVal.toFixed(0)}</td>
                      <td style={{ padding: "6px 10px", color: "#111" }}>${r.spreadValue.toFixed(0)}</td>
                      <td style={{ padding: "6px 10px", fontWeight: 700, color: profit ? C.green : C.red }}>
                        {profit ? "+" : ""}${r.netPL.toFixed(0)}
                      </td>
                      <td style={{ padding: "6px 10px", fontWeight: 600, color: profit ? C.green : C.red }}>
                        {profit ? "+" : ""}{r.ror.toFixed(0)}%
                      </td>
                      <td style={{ padding: "6px 10px", fontSize: 10, fontWeight: 700, color: marker ? marker.color : C.gray, whiteSpace: "nowrap" }}>
                        {marker ? marker.label : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
